/**
 * 「行ったことのある県」の印（旅好きの人・旅系の発信者向け）。
 *
 * - 県えらびの一覧（トップ・ゲーム中の地域えらび）で、県ごとに「行った」を付け外しできる
 * - 日本のタイル地図にも小さな印を出す
 * - 端末の中にだけ保存する（新しいキー `meshi-merge:visited:v1`）。トロフィー・メダルには絡めない
 */

import { metaStorageKey } from '../game/config/gameConfig';
import type { StorageLike } from '../game/systems/StorageSystem';
import { JsonStore } from '../meta/jsonStore';

interface VisitedData {
  regions: string[];
}

function normalize(raw: unknown): VisitedData {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const list = Array.isArray(r.regions) ? r.regions.filter((x): x is string => typeof x === 'string') : [];
  return { regions: [...new Set(list)] };
}

export class VisitedStore {
  private readonly store: JsonStore<VisitedData>;

  constructor(storage?: StorageLike | null) {
    this.store = new JsonStore(metaStorageKey('visited'), normalize, storage);
  }

  has(regionId: string): boolean {
    return this.store.data.regions.includes(regionId);
  }

  get count(): number {
    return this.store.data.regions.length;
  }

  /** 付け外しして、付いた状態なら true を返す。 */
  toggle(regionId: string): boolean {
    const on = !this.has(regionId);
    this.store.update((d) => {
      d.regions = on ? [...d.regions, regionId] : d.regions.filter((r) => r !== regionId);
    });
    return on;
  }
}

/**
 * 一覧の行（県を選ぶボタン）の横に「行った」ボタンを並べる。
 * 行は `data-region` を持っている必要がある。ボタンの中にボタンは置けないので、行を包む。
 */
export function decorateVisitedRows(
  root: ParentNode,
  rowSelector: string,
  store: VisitedStore,
  onChange?: () => void,
): void {
  root.querySelectorAll<HTMLElement>(`${rowSelector}[data-region]`).forEach((row) => {
    const regionId = row.dataset.region!;
    if (row.parentElement?.classList.contains('visited-row')) return;
    const wrap = document.createElement('div');
    wrap.className = 'visited-row';
    row.replaceWith(wrap);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'visited-toggle';
    const name = row.querySelector('[class$="__name"]')?.firstChild?.textContent?.trim() ?? '';
    const render = () => {
      const on = store.has(regionId);
      // 押す前から「行った」とだけ書いてあると、もう印が付いているように見えた（2026-10-05）。
      // チェックの箱の形にして、付いている／いないを見分けられるようにする
      btn.textContent = on ? '☑ 行った' : '☐ 行った';
      btn.dataset.on = String(on);
      btn.setAttribute('aria-pressed', String(on));
      // 押した／押していないは aria-pressed で伝える。名前は状態で変えない（「印を外す」と言いながら押されている、を防ぐ）
      btn.setAttribute('aria-label', `${name}に行った`);
    };
    render();
    btn.addEventListener('click', (event) => {
      event.stopPropagation();
      store.toggle(regionId);
      render();
      onChange?.();
    });
    wrap.append(row, btn);
  });
}

/** タイル地図の印を付け直す（タイルは `data-region` を持っている必要がある）。 */
export function markVisitedTiles(root: ParentNode, store: VisitedStore): void {
  root.querySelectorAll<HTMLElement>('.tile-map__tile[data-region]').forEach((tile) => {
    const on = store.has(tile.dataset.region!);
    tile.dataset.visited = String(on);
    const label = tile.getAttribute('aria-label')?.replace(/・行った$/, '') ?? '';
    tile.setAttribute('aria-label', on ? `${label}・行った` : label);
  });
}
